<template>
  <section v-bind="$attrs" id="cars-carousel" class="-me-16 space-y-24 md:mx-0">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-16">
        <div class="text-title font-semi-bold text-dark">
          {{ `${$t('car')} ${$t('results')}` }}
        </div>

        <div
          v-if="
            !isLoadingSearchAndFilter && carSearchResultStore.totalCarCount > 0
          "
          class="text-subject text-gray-600"
        >
          ({{
            $t('search.nResults', {
              count: carSearchResultStore.totalCarCount,
            })
          }})
        </div>
      </div>

      <div class="ms-24">
        <JPagination
          v-if="isResultsWithMinimumSize && !isLoadingSearchAndFilter"
          :modelValue="page"
          justify="end"
          :showElipsis="false"
          :maxVisibleButtons="3"
          color="gray-300"
          :disabled="isLoadingCarPages"
          buttonClass="!text-gray-600 !w-40 !h-40"
          arrowButtonClass="!w-40 !h-40 border border-gray-300 !text-gray-600 disabled:!text-gray-300"
          class="gap-8 [&_div]:gap-8"
          :totalPages="totalPages"
          @update:model-value="onChangePage"
        />
      </div>
    </div>

    <JRow
      v-if="!flightSearchResultsStore.isSearchEmpty"
      spaceMd="8"
      spaceLg="8"
      spaceXl="8"
    >
      <JCol md="3" lg="3" xl="3">
        <div class="flex h-full items-center gap-8">
          <span class="text-subject font-bold text-dark">
            {{ $t('filter') }}
          </span>

          <JButton
            v-if="!isLoadingSearchAndFilter"
            size="xs"
            color="secondary"
            variant="tonal"
            @click="onResetForm"
          >
            {{ $t('resetAll') }}
          </JButton>
        </div>
      </JCol>

      <JCol md="9" lg="9" xl="9" class="flex items-center gap-16">
        <div
          v-if="
            isResultsWithMinimumSize &&
            !isLoadingSearchAndFilter &&
            searchPanelHistory.history.isMultiDestination
          "
          class="h-20 border border-gray-300"
        />

        <MultiCitySearchResultSelector
          v-if="
            !carSearchResultStore.isSearchEmpty &&
            searchPanelHistory.history.isMultiDestination
          "
          v-model="city"
          :disabled="isLoadingSearchAndFilter || isLoadingCarPages"
          includeHotelOptions
          @select="onSelectCity"
        />
      </JCol>
    </JRow>

    <JRow spaceMd="8" spaceLg="8" spaceXl="8">
      <!-- FILTER -->
      <JCol
        v-if="!carSearchResultStore.isSearchEmpty"
        md="3"
        lg="3"
        xl="2"
        class="relative flex min-h-[40.5rem] flex-col"
      >
        <CarResultsFilter
          ref="carResultsFilterRef"
          class="absolute inset-x-8 bottom-0 top-0 h-full overflow-y-auto"
          @submit="onSubmitForm"
        />
      </JCol>

      <JCol
        :md="carSearchResultStore.isSearchEmpty ? '12' : '9'"
        :lg="carSearchResultStore.isSearchEmpty ? '12' : '9'"
        :xl="carSearchResultStore.isSearchEmpty ? '12' : '10'"
      >
        <JCarousel
          ref="carouselRef"
          :slidesPerView="isMobile ? 1.5 : 1"
          :spaceBetween="isMobile ? 16 : 30"
          :navigation="false"
          :allowTouchMove="!isLoadingCarPages"
          :class="{
            'pointer-events-none': isLoadingCarPages,
          }"
          class="pr-16 md:pr-0 [&_div.text-title.text-dark]:pl-16 [&_div.text-title.text-dark]:md:pl-0"
          :a11y="{
            enabled: false,
          }"
          @slidechange="onSlideChange"
        >
          <template
            v-if="
              carSearchResultStore.carsList.length || isLoadingSearchAndFilter
            "
          >
            <template v-if="isLoadingSearchAndFilter">
              <JCarouselItem class="grid grid-cols-4 gap-20">
                <CarCardSkeleton v-for="i in 4" :key="i" />
              </JCarouselItem>
            </template>

            <template v-else>
              <JCarouselItem
                v-for="item in slidesCount"
                :key="item"
                class="grid grid-cols-4 gap-20"
              >
                <CarCard
                  v-for="car in getCarList(item)"
                  :key="car.uniqueId"
                  :car="car"
                  @show-detail="showCarDetail(car)"
                  @select="onSelect(car.uniqueId)"
                  @delete="onDeselect(car.uniqueId)"
                />

                <CarCardSkeleton
                  v-for="i in getSkeletonList(item)"
                  :key="i"
                  class="h-full"
                />
              </JCarouselItem>
            </template>
          </template>

          <template v-else>
            <NoSearchResult
              :image="{ src: '/car.svg', alt: 'Car' }"
              :description="$t('search.noResultFound', { title: $t('car') })"
              class="flex min-h-[40.5rem] w-full flex-col justify-center"
            />
          </template>
        </JCarousel>
      </JCol>
    </JRow>
  </section>

  <!-- DETAIL -->
  <JBottomSheet
    v-if="isMobile"
    v-model="carDetailIsOpen"
    fullScreen
    hasPopstate
  >
    <template #header>
      <JContainer class="flex justify-center p-16">
        <TheMobileHeader
          titleClass="text-dark font-bold text-base"
          showBack
          @back="setCarDetailOpen(false)"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" @retry="refetch" />

    <CarDetail
      v-else
      :isLoading="carDetailIsFetching"
      @select="onSelect"
      @delete="onDeselect"
    />
  </JBottomSheet>

  <JDrawer v-else v-model="carDetailIsOpen" floatClose>
    <ServerError v-if="isError" @retry="refetch" />

    <CarDetail
      v-else
      :isLoading="carDetailIsFetching"
      @select="onSelect"
      @delete="onDeselect"
    />
  </JDrawer>
</template>

<script setup lang="ts">
import {
  JBottomSheet,
  JButton,
  JCarousel,
  JCarouselItem,
  JCol,
  JContainer,
  JDrawer,
  JPagination,
  JRow,
} from '@journey/ui-kit'
import { useI18n } from 'vue-i18n'
import { useToggle } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue'
import CarResultsFilter from '../CarResultsFilter/CarResultsFilter.vue'
import useCarSearchResultsStore from '@/modules/Search/store/carSearchResults'
import TheMobileHeader from '@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue'
import CarCard from '@/modules/Core/components/shared/CarCard/CarCard.vue'
import CarCardSkeleton from '@/modules/Core/components/shared/CarCard/CarCardSkeleton/CarCardSkeleton.vue'
import CarDetail from '@/modules/Core/components/shared/CarDetail/CarDetail.vue'
import { useDevicePlatform } from '@/modules/Core/composables'
import NoSearchResult from '@/modules/Search/components/NoSearchResult/NoSearchResult.vue'
import { useCarInfoQuery } from '@/modules/Search/requests/queries'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import type { Car } from '@/modules/Search/types/model/search'
import type { SearchPanelCarFilters } from '@/modules/Search/types'
import {
  FilteredCarListSortBy,
  SearchOrFilterMode,
} from '@/modules/Search/types'
import type { FilteredCarListPayload } from '@/modules/Search/types/api/payload/filteredCarList'
import ServerError from '@/modules/Core/components/shared/ServerError/ServerError.vue'
import { useFilteredCarListMutation } from '@/modules/Search/requests/mutations'
import useCarSearchFiltersStore from '@/modules/Search/store/carSearchFilters'
import {
  DEFAULT_CAR_PAGE_SIZE,
  MINIMUM_RESULT_SIZE,
} from '@/modules/Search/constants'
import useSearchStore from '@/modules/Search/store'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import { useItinerary } from '@/modules/Search/composables'
import useToastStore from '@/modules/Core/store/toast'
import useFlightSearchResultsStore from '@/modules/Search/store/flightSearchResults'
import useAuthStore from '@/modules/Auth/store'
import featureFlag from '@/config/featureFlag'

defineOptions({
  inheritAttrs: false,
})

const MultiCitySearchResultSelector = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/MultiCitySearchResultSelector/MultiCitySearchResultSelector.vue'
    )
)

const { isMobile } = useDevicePlatform()

const itinerarySummaryStore = useItinerarySummary()
const carSearchResultStore = useCarSearchResultsStore()
const carSearchFiltersStore = useCarSearchFiltersStore()
const searchPanelHistory = useSearchPanelHistoryStore()
const { submitItinerary } = useItinerary()
const authStore = useAuthStore()

const [carDetailIsOpen, setCarDetailOpen] = useToggle(false)

const provider = ref('')
const pickUpDate = ref('')
const dropOffDate = ref('')
const uniqueId = ref('')
const carCode = ref('')
const supplierCode = ref('')
const searchTerm = ref('')
const searchId = computed(() => searchPanelHistory.history.searchId ?? '')
const searchOption = ref(0)
const customerCode = ref(featureFlag.customerCode)
const flightSearchResultsStore = useFlightSearchResultsStore()
const city = ref()

const {
  data: carDetail,
  isFetching: carDetailIsFetching,
  isError,
  refetch,
} = useCarInfoQuery(searchId, searchOption, customerCode, {
  /* FIXME: `refetch` function doesn't work as expected. It returns
   *  `undefined` when called. For now, we enable the query by passing a
   *  `Ref<Boolean>` which behaves correctly.
   */
  enabled: carDetailIsOpen,
})

function showCarDetail(payload: Car) {
  /* TODO: The provider should be dynamic and filled with the proper name of
   *  the provider returned by the API. For now, the functionality is not
   *  implemented in the API side, so we hard-code the value.
   */
  provider.value = 'FlexibleAutos'
  pickUpDate.value = payload.pickupDateTime
  dropOffDate.value = payload.dropoffDateTime
  carCode.value = payload.code
  uniqueId.value = payload.uniqueId
  supplierCode.value = payload.supplierCode
  searchTerm.value = payload.iataCode
  searchOption.value = payload.searchOption

  setCarDetailOpen(true)
}

watch(carDetail, (value) => {
  if (!value || !value.data) {
    return
  }

  carSearchResultStore.setSelectedCarInfo(value.data)
})

const toastStore = useToastStore()
const { t } = useI18n()

async function onSelect(uniqueId: string) {
  const car = carSearchResultStore.findCarByUniqueId(uniqueId)

  if (!car) {
    return
  }

  itinerarySummaryStore.select(car)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  toastStore.addToast({
    title: t('search.itinerarySummary.addMessages.carAdded'),
    color: 'success',
  })

  if (!carDetailIsOpen.value) {
    return
  }

  setCarDetailOpen(false)
}

async function onDeselect(uniqueId: string) {
  itinerarySummaryStore.remove(uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)
}

const page = ref(1)
const apiPage = ref(1)
const size = ref(DEFAULT_CAR_PAGE_SIZE)
const query = ref('')

const { isLoading: isLoadingCarPages, mutateAsync } =
  useFilteredCarListMutation({
    meta: {
      noSuccessToast: true,
    },
  })

const apiPayload = reactive<FilteredCarListPayload>({
  searchId: '',
  sortBy: carSearchFiltersStore.filters.sortBy,
  carType: carSearchFiltersStore.filters.sortCarType!,
  capacity: carSearchFiltersStore.filters.capacity,
  query: query.value,
  page: 1,
  size: size.value,
  residency: authStore.userResidency,
})

const searchStore = useSearchStore()

const [isLoadingFilter, toggleIsFilterLoading] = useToggle()

async function onSubmitFilters() {
  searchStore.setTriggeredBy(SearchOrFilterMode.Filter)

  apiPayload.searchId = searchId.value

  const { data } = await mutateAsync(apiPayload, {
    onError: () => {
      if (apiPayload.page === 1) {
        carSearchResultStore.setTotalCarPages(0)
        carSearchResultStore.setTotalCarCount(0)
      }
      toggleIsFilterLoading(false)
    },
  })

  if (!data) {
    return
  }

  const { items, page } = data.data

  carSearchResultStore.appendCars(items)

  if (page.items !== carSearchResultStore.totalCarCount) {
    carSearchResultStore.setTotalCarCount(page.items ?? 1)
  }
}

async function onSelectCity(value?: string) {
  toggleIsFilterLoading(true)

  if (value) {
    apiPayload.destinations = [value.toLocaleLowerCase()]
  } else {
    delete apiPayload.destinations
  }

  apiPage.value = 1
  apiPayload.page = 1

  carSearchResultStore.resetCars()

  await onSubmitFilters()

  toggleIsFilterLoading(false)
}

async function onSubmitForm(filters: SearchPanelCarFilters) {
  toggleIsFilterLoading(true)

  apiPage.value = 1
  Object.assign(apiPayload, {
    sortBy: filters.sortBy,
    carType: filters.sortCarType!,
    capacity: filters.capacity,
    ...(filters.sortBy &&
      filters.sortBy === FilteredCarListSortBy.CarType && {
        sortCarType: filters.sortCarType,
      }),
    companies: filters.companies,
    page: 1,
    ...(city.value && {
      destinations: [city.value.toLocaleLowerCase()],
    }),
  } as SearchPanelCarFilters)

  if (filters.sortBy !== FilteredCarListSortBy.CarType) {
    delete apiPayload.sortCarType
  }

  carSearchResultStore.resetCars()

  await onSubmitFilters()

  toggleIsFilterLoading(false)
}

watch(isLoadingFilter, carSearchFiltersStore.setLoading)

const totalPages = computed(() =>
  Math.ceil(carSearchResultStore.totalCarCount / MINIMUM_RESULT_SIZE)
)

const isLoadingSearchAndFilter = computed(
  () => carSearchResultStore.isLoading || isLoadingFilter.value
)

const isResultsWithMinimumSize = computed(
  () => carSearchResultStore.totalCarCount > MINIMUM_RESULT_SIZE
)

const slidesCount = computed(() => {
  const { carsList } = carSearchResultStore
  const loadedCount = Math.ceil(carsList.length / MINIMUM_RESULT_SIZE)

  return Math.min(loadedCount + MINIMUM_RESULT_SIZE, totalPages.value)
})

const carouselRef = ref<InstanceType<typeof JCarousel>>()

watch(isLoadingSearchAndFilter, async () => {
  await nextTick()
  carouselRef.value?.mainSwiper?.swiper.update()
})

function onSlideChange(event: any) {
  const activeIndex = event.target.swiper?.activeIndex

  if (activeIndex === undefined) {
    return
  }

  if (page.value === activeIndex + 1) {
    return
  }

  page.value = activeIndex + 1

  getFetchOrScrollCarStatus()
}

function scrollToIndex() {
  carouselRef.value?.mainSwiper?.swiper.slideTo(page.value - 1)
}

function onChangePage(value: number) {
  if (page.value === value) {
    return
  }

  page.value = value

  getFetchOrScrollCarStatus()
}

function getFetchOrScrollCarStatus() {
  if (page.value * MINIMUM_RESULT_SIZE > apiPage.value * size.value) {
    apiPage.value++
    apiPayload.page = apiPage.value

    scrollToIndex()
    onSubmitFilters()

    return
  }

  scrollToIndex()
}

function getCarList(item: number) {
  if (Math.abs(item - page.value) > 1) {
    return []
  }

  const start = (item - 1) * MINIMUM_RESULT_SIZE
  const end = item * MINIMUM_RESULT_SIZE

  return carSearchResultStore.carsList.slice(start, end)
}

function slideHasData(item: number) {
  const totalItems = carSearchResultStore.carsList.length
  const start = (item - 1) * MINIMUM_RESULT_SIZE

  return start < totalItems
}

function getSkeletonList(slideIndex: number) {
  if (slideHasData(slideIndex)) {
    return []
  }

  return Array.from({ length: MINIMUM_RESULT_SIZE }, (_, i) => i)
}

function resetCarList() {
  if (carSearchResultStore.carsList.length < DEFAULT_CAR_PAGE_SIZE) {
    return
  }

  carSearchResultStore.setCars(
    carSearchResultStore.carsList.slice(0, DEFAULT_CAR_PAGE_SIZE)
  )
}

onUnmounted(resetCarList)

watch(
  () => carSearchResultStore.isLoading,
  () => {
    apiPage.value = 1
    city.value = undefined
    apiPayload.destinations = undefined
  }
)

watch(
  () => flightSearchResultsStore.filteredCity,
  () => {
    city.value = flightSearchResultsStore.filteredCity
    apiPayload.destinations = city.value ? [city.value.toLocaleLowerCase()] : []
  }
)

const carResultsFilterRef = ref<InstanceType<typeof CarResultsFilter>>()

function onResetForm() {
  carResultsFilterRef.value?.onResetForm()
}

defineExpose({
  onSelectCity,
})
</script>
